import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { AddIdField, IIndividualList, QPFQuickRegister } from "@4uhub/lib4uhub";
import dayjs, { Dayjs } from "dayjs";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import ProcedureType from "../../components/Exams/ProcedureType/ProcedureType";
import { DateRange } from "../../components/UI/DateRange/DateRange";
import useFetch from "../../hooks/useFetch";
import { getEstablishmentParam } from "../../services/establishmentParam.service";
import { IManagementLaboratorialExamsFilter } from "./ManagementLaboratorialExamsPage";

interface IFilterProps {
  filter: IManagementLaboratorialExamsFilter;
  onFilterChange: (v: IManagementLaboratorialExamsFilter) => void;
}

type PeriodOption = {
  value: string;
  name: string;
  startDate?: Dayjs;
  endDate?: Dayjs;
};

const ManagementLaboratorialExamsFilter: React.FC<IFilterProps> = ({
  filter,
  onFilterChange,
}) => {
  const [showProcedureTypeFilter, setShowProcedureTypeFilter] = useState(false);
  const [individual, setIndividual] =
    useState<AddIdField<IIndividualList> | null>(null);
  const [period, setPeriod] = useState("today");

  const { sendRequest } = useFetch(getEstablishmentParam);

  const periodOptions = useMemo<PeriodOption[]>(() => {
    const today = dayjs();

    return [
      { value: "today", name: "Hoje", startDate: today, endDate: today },
      {
        value: "week",
        name: "Esta semana",
        startDate: today.startOf("week"),
        endDate: today,
      },
      {
        value: "month",
        name: "Este mes",
        startDate: today.startOf("month"),
        endDate: today,
      },
      {
        value: "previousMonth",
        name: "Mes anterior",
        startDate: today.subtract(1, "month").startOf("month"),
        endDate: today.subtract(1, "month").endOf("month"),
      },
      {
        value: "last30Days",
        name: "Ultimos 30 dias",
        startDate: today.subtract(29, "day"),
        endDate: today,
      },
      { value: "custom", name: "Selecionar periodo" },
    ];
  }, []);

  const fetchEstablishmentParam = useCallback(async () => {
    const { data, success } = await sendRequest("29");
    if (data && success) {
      setShowProcedureTypeFilter(data.length > 0);
    }
  }, [sendRequest]);

  const onChangeProcedureTypeHandler = useCallback(
    (value: string) => {
      onFilterChange({
        ...filter,
        ExamType: value,
      });
    },
    [filter, onFilterChange],
  );

  const onSelectedIndividual = useCallback(
    (value: AddIdField<IIndividualList> | null) => {
      setIndividual(value);
      onFilterChange({
        ...filter,
        IndividualId: value ? value.id : undefined,
      });
    },
    [filter, onFilterChange],
  );

  const onChangePeriod = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      setPeriod(value);

      const selected = periodOptions.find((option) => option.value === value);
      if (!selected?.startDate || !selected.endDate) return;

      const endDate =
        selected.endDate.diff(selected.startDate, "day") > 30
          ? selected.startDate.add(30, "day")
          : selected.endDate;

      onFilterChange({
        ...filter,
        StartDate: selected.startDate,
        EndDate: endDate,
      });
    },
    [filter, onFilterChange, periodOptions],
  );

  const onChangeEndDateHandler = useCallback(
    (endDate: Dayjs | null) => {
      setPeriod("custom");
      onFilterChange({ ...filter, EndDate: endDate });
    },
    [onFilterChange, filter],
  );

  const onStartDateChangeHandler = useCallback(
    (startDate: Dayjs | null) => {
      setPeriod("custom");

      if (startDate && filter.EndDate) {
        const needToAdd =
          filter.EndDate.diff(startDate, "day") > 30 ||
          filter.EndDate.diff(startDate, "day") < 0;

        onFilterChange({
          ...filter,
          StartDate: startDate,
          EndDate: needToAdd ? startDate.add(30, "day") : filter.EndDate,
        });
        return;
      }

      onFilterChange({ ...filter, StartDate: startDate });
    },
    [filter, onFilterChange],
  );

  const maxEndDate = useMemo(
    () => filter.StartDate?.add(30, "day"),
    [filter.StartDate],
  );

  useEffect(() => {
    fetchEstablishmentParam();
  }, [fetchEstablishmentParam]);

  return (
    <Stack
      pt={1}
      flexDirection={"row"}
      alignItems={"start"}
      flexWrap={"wrap"}
      gap={1}
    >
      {showProcedureTypeFilter && (
        <ProcedureType
          procedureType={filter.ExamType}
          changeProcedureType={onChangeProcedureTypeHandler}
        />
      )}
      <FormControl sx={{ minWidth: 225 }}>
        <InputLabel id="management-exams-period-label">
          Intervalo de Data
        </InputLabel>
        <Select
          labelId="management-exams-period-label"
          value={period}
          label="Intervalo de Data"
          size="small"
          onChange={onChangePeriod}
        >
          {periodOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <DateRange
        startDateValue={filter.StartDate}
        endDateValue={filter.EndDate}
        maxEndDate={maxEndDate}
        onChangeStartDate={onStartDateChangeHandler}
        onChangeEndDate={onChangeEndDateHandler}
        disableFuture
        clearable={false}
      />
      <Stack sx={{ minWidth: 320 }}>
        <QPFQuickRegister
          label="Paciente"
          value={individual}
          disableRegister
          onChange={onSelectedIndividual}
          withBirthDate={true}
        />
      </Stack>
    </Stack>
  );
};

export default memo(ManagementLaboratorialExamsFilter);
